import { StyleSheet } from '@react-pdf/renderer';

export const colors = {
  primary: '#7755cc',
  primaryDark: '#4b3280',
  primaryLight: '#f3f0ff',
  foreground: '#495057',
  muted: '#f8f9fa',
  mutedFg: '#868e96',
  border: '#e9ecef',
  card: '#ffffff',
  white: '#ffffff',
  gray950: '#18181b',
  zinc500: '#71717a',
  fundingHeaderBg: '#f7f5fd',
  summaryBg: '#7c5dc7',
  summaryDark: '#5b45a0',
  summaryDisclaimer: '#6d48c9',
  progressEarly: '#c4b5fd',
  progressMod: '#8b5cf6',
  progressFull: '#5b21b6',
  red: '#ef4444',
  green: '#22c55e',
  detailBorder: '#f1f5f9',
  disclosureBg: '#f5f3fa',
  disclosureBorder: '#ebe5f5',
};

export const onboardingPdfReportStyle = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: colors.foreground,
    paddingTop: 28,
    paddingBottom: 44,
    paddingHorizontal: 30,
    backgroundColor: colors.muted,
  },

  // Header
  headerCard: {
    backgroundColor: colors.white,
    border: `0.75pt solid ${colors.border}`,
    borderRadius: 12,
    paddingTop: 24,
    paddingBottom: 16,
    paddingHorizontal: 24,
    marginBottom: 24,
  },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' },
  orgName: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    color: colors.foreground,
    marginBottom: 1,
  },
  orgSubtitle: { fontSize: 8, color: colors.foreground, marginBottom: 4 },
  reportTagRow: { flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: 6 },
  reportTag: { fontSize: 9, color: colors.primary, fontFamily: 'Helvetica-Bold' },
  clientName: {
    fontSize: 28,
    fontFamily: 'Helvetica-Bold',
    color: colors.gray950,
    marginBottom: 8,
    letterSpacing: -0.4,
  },
  reportDesc: { fontSize: 9, color: colors.mutedFg, lineHeight: 1.5, maxWidth: 340 },
  dateLabel: { fontSize: 7.5, color: colors.mutedFg, textAlign: 'right' },
  dateValue: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    color: colors.primary,
    marginTop: 2,
    textAlign: 'right',
  },

  // Predictions
  predictionGrid: { flexDirection: 'row', gap: 8, marginBottom: 14 },
  predictionCard: {
    flex: 1,
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
  },
  predictionLabel: {
    fontSize: 6,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 2,
    color: 'rgba(255,255,255,0.85)',
  },
  predictionValue: {
    fontSize: 16,
    fontFamily: 'Helvetica-Bold',
    color: colors.white,
    lineHeight: 1.2,
  },
  predictionSubtitle: { fontSize: 6, color: 'rgba(255,255,255,0.7)', marginTop: 2 },

  // Section headers
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
    marginBottom: 8,
  },
  sectionIconBox: {
    width: 24,
    height: 24,
    borderRadius: 6,
    backgroundColor: colors.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sectionTitle: { fontSize: 13, fontFamily: 'Helvetica-Bold', color: colors.foreground },

  // Projected care costs
  costsBox: {
    backgroundColor: colors.white,
    border: `0.75pt solid ${colors.border}`,
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 20,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderBottom: `0.75pt solid ${colors.border}`,
  },
  totalCostLabel: { fontSize: 10, color: colors.zinc500, fontFamily: 'Helvetica-Bold' },
  totalCostValue: { fontSize: 16, fontFamily: 'Helvetica-Bold', color: colors.primary },

  // Phase cards
  phasesRow: { flexDirection: 'row', gap: 10, paddingVertical: 14, paddingHorizontal: 14 },
  phaseCard: {
    flex: 1,
    backgroundColor: colors.white,
    border: `0.75pt solid ${colors.border}`,
    borderRadius: 10,
    overflow: 'hidden',
  },
  phaseHeader: {
    backgroundColor: colors.primaryLight,
    borderBottom: `0.75pt solid ${colors.border}`,
    paddingVertical: 12,
    paddingHorizontal: 12,
    alignItems: 'center',
  },
  phaseLabel: {
    fontSize: 6.5,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    color: colors.zinc500,
    marginBottom: 3,
  },
  phaseValue: {
    fontSize: 15,
    fontFamily: 'Helvetica-Bold',
    color: colors.primary,
    marginBottom: 2,
  },
  phaseSubtitle: { fontSize: 7.5, color: colors.zinc500 },
  phaseBody: { paddingHorizontal: 12, paddingTop: 6, paddingBottom: 8 },
  phaseDetail: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 6,
    borderBottom: `0.75pt solid ${colors.detailBorder}`,
  },
  phaseDetailLabel: { fontSize: 8, color: colors.zinc500 },
  phaseDetailValue: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: colors.gray950 },

  // Progress bar
  progressBarWrapper: { paddingHorizontal: 14, paddingBottom: 14 },
  progressBar: {
    flexDirection: 'row',
    height: 8,
    borderRadius: 99,
    overflow: 'hidden',
    backgroundColor: colors.muted,
  },

  // Care hours
  hoursBox: {
    backgroundColor: colors.card,
    border: `0.75pt solid ${colors.border}`,
    borderRadius: 10,
    padding: 10,
    marginBottom: 10,
  },
  hoursTitle: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: colors.foreground,
    marginBottom: 6,
  },
  hoursLabelRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 },
  hoursLabel: { fontSize: 7.5, color: colors.mutedFg },
  hoursValue: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: colors.foreground },
  hoursBarBg: { height: 5, backgroundColor: colors.muted, borderRadius: 99, marginBottom: 6 },
  hoursBarFill: { height: 5, borderRadius: 99, backgroundColor: colors.primary },
  hoursTable: { marginTop: 6 },
  hoursTableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: colors.muted,
    borderBottom: `0.75pt solid ${colors.border}`,
  },
  hoursTableRow: { flexDirection: 'row', borderBottom: `0.75pt solid ${colors.border}` },
  hoursTableCell: { flex: 1, fontSize: 7, padding: 3, color: colors.foreground },
  hoursTableHeader: {
    flex: 1,
    fontSize: 6,
    fontFamily: 'Helvetica-Bold',
    padding: 3,
    color: colors.mutedFg,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },

  // Funding cards
  fundingCard: {
    flex: 1,
    backgroundColor: colors.card,
    border: `0.75pt solid ${colors.border}`,
    borderRadius: 10,
    overflow: 'hidden',
  },
  fundingHeaderSection: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottom: `0.75pt solid ${colors.border}`,
    backgroundColor: colors.fundingHeaderBg,
  },
  fundingHeaderTitle: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    color: colors.gray950,
    marginBottom: 10,
  },
  fundingHeaderGrid: { flexDirection: 'row', gap: 12 },
  fundingHeaderItem: { flex: 1, alignItems: 'center' },
  fundingHeaderLabel: {
    fontSize: 6,
    fontFamily: 'Helvetica-Bold',
    color: colors.mutedFg,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    marginBottom: 3,
    textAlign: 'center',
  },
  fundingHeaderValue: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: colors.gray950,
    textAlign: 'center',
  },
  fundingRowsWrapper: { paddingTop: 4 },
  fundingRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 7,
    paddingHorizontal: 16,
    borderBottom: `0.75pt solid ${colors.muted}`,
  },
  fundingRowEven: { backgroundColor: colors.muted },
  fundingRowLabel: { fontSize: 8.5, color: colors.foreground },
  fundingRowValue: {
    fontSize: 8.5,
    fontFamily: 'Helvetica-Bold',
    color: colors.foreground,
    textAlign: 'right',
  },
  fundingNote: {
    paddingHorizontal: 16,
    paddingVertical: 6,
    fontSize: 6.5,
    color: colors.mutedFg,
    lineHeight: 1.3,
  },
  fundingDisclaimer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 7,
    marginBottom: 8,
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderWidth: 1,
    borderColor: colors.disclosureBorder,
    borderRadius: 4,
    backgroundColor: colors.primaryLight,
  },
  fundingDisclaimerContent: {
    flex: 1,
  },
  fundingDisclaimerText: {
    fontSize: 7,
    color: colors.primaryDark,
    lineHeight: 1.35,
  },
  fundingFootnoteText: {
    fontSize: 6.5,
    color: colors.mutedFg,
    lineHeight: 1.3,
    marginBottom: 1,
  },
  primaryValue: { color: colors.primary },

  // Combined summary
  summaryWrap: { borderRadius: 10, overflow: 'hidden', marginTop: 6 },
  summaryRow: { flexDirection: 'row', backgroundColor: colors.summaryBg },
  summaryItem: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 6,
    minHeight: 65,
  },
  summaryItemDark: { backgroundColor: colors.summaryDark },
  summaryLabel: {
    fontSize: 6.5,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    color: 'rgba(255,255,255,0.9)',
    marginBottom: 4,
  },
  summaryValue: { fontSize: 15, fontFamily: 'Helvetica-Bold', color: colors.white },
  summaryDisclaimer: {
    paddingVertical: 7,
    paddingHorizontal: 12,
    backgroundColor: colors.summaryDisclaimer,
    fontSize: 7.5,
    textAlign: 'center',
    color: 'rgba(255,255,255,0.9)',
    lineHeight: 1.3,
  },

  // Optional sections
  optionalSectionBox: {
    backgroundColor: colors.white,
    borderRadius: 10,
    marginBottom: 8,
    border: `0.75pt solid ${colors.border}`,
  },
  optionalSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 5,
    paddingHorizontal: 8,
    backgroundColor: colors.disclosureBg,
    borderBottom: `0.75pt solid ${colors.border}`,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
  optionalSectionHeaderText: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: colors.foreground,
  },
  optionalSectionBody: { padding: 10, paddingTop: 8 },
  optionalNarrative: { fontSize: 7.5, color: '#52525b', marginBottom: 5 },
  optionalGrid: { flexDirection: 'row', gap: 8 },
  illustrationCard: {
    flex: 1,
    backgroundColor: '#f5f5f7',
    border: `0.75pt solid ${colors.border}`,
    borderRadius: 8,
  },
  illustrationWrapper: {
    padding: 6,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 80,
  },
  illustrationImage: { width: 100, height: 100 },
  illustrationTitle: {
    paddingHorizontal: 6,
    paddingTop: 4,
    fontSize: 7.5,
    fontFamily: 'Helvetica-Bold',
    color: '#3f3f46',
  },
  illustrationSubtitle: { paddingHorizontal: 6, paddingBottom: 5, fontSize: 6.5, color: '#71717a' },
  optionalStatsRow: { flexDirection: 'row', gap: 8, marginTop: 8 },
  optionalStatItem: {
    flex: 1,
    alignItems: 'center',
    borderRadius: 6,
    border: `0.75pt solid ${colors.border}`,
    backgroundColor: '#f5f5fa',
    paddingVertical: 5,
  },
  optionalStatLabel: {
    fontSize: 5.5,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    color: '#71717a',
  },
  optionalStatValue: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: colors.primaryDark,
    marginTop: 1,
  },
  optionalStatUnit: { fontSize: 6, color: '#71717a', marginTop: 1 },
  centerDuration: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 1 },
  centerDurationLabel: {
    fontSize: 6,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    color: '#71717a',
  },
  centerDurationValue: { fontSize: 14, fontFamily: 'Helvetica-Bold', color: colors.primaryDark },
  centerDurationUnit: { fontSize: 6.5, color: '#52525b' },
  centerDurationLine: { marginTop: 2, width: 80, height: 1, backgroundColor: '#7c5dc7' },
  lateCareCard: { borderColor: '#b7a3e6' },
  hoursPill: {
    marginHorizontal: 6,
    marginBottom: 5,
    borderRadius: 5,
    backgroundColor: '#ffffff',
    textAlign: 'center',
    paddingVertical: 3,
  },
  hoursPillValue: { fontSize: 11, fontFamily: 'Helvetica-Bold', color: colors.primaryDark },
  hoursPillUnit: { fontSize: 6, color: '#71717a' },

  // Care age timeline
  ageTimelineContainer: { marginTop: 12, paddingHorizontal: 4 },
  ageTimelineRow: { flexDirection: 'row' },
  ageTimelineSegment: { flex: 1, marginRight: 6 },
  ageTimelineSegmentLabel: {
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    color: colors.foreground,
    textAlign: 'center',
    marginBottom: 3,
  },
  ageTimelineSegmentLine: { height: 2, backgroundColor: colors.primary, marginBottom: 3 },
  ageTimelineSegmentLineEarly: {
    backgroundColor: '#c4b5fd',
    borderTopLeftRadius: 1,
    borderBottomLeftRadius: 1,
  },
  ageTimelineSegmentLineMod: { backgroundColor: '#8b5cf6' },
  ageTimelineSegmentLineFull: {
    backgroundColor: '#5b21b6',
    borderTopRightRadius: 1,
    borderBottomRightRadius: 1,
  },
  ageTimelineSegmentAges: { flexDirection: 'row', justifyContent: 'space-between' },
  ageTimelineAge: { fontSize: 6.5, color: colors.mutedFg, textAlign: 'center' },

  // Disclosures
  disclosureBox: {
    marginTop: 6,
    backgroundColor: colors.disclosureBg,
    border: `0.75pt solid ${colors.border}`,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 6,
  },
  disclosureTitle: {
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    color: colors.primary,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    marginBottom: 5,
  },
  disclosureText: { fontSize: 6, color: '#6b7280', lineHeight: 1.4, marginBottom: 4 },

  // ROI appendix
  appendixBox: {
    borderRadius: 10,
    overflow: 'hidden',
    border: `0.75pt solid ${colors.border}`,
    backgroundColor: colors.disclosureBg,
    marginTop: 12,
  },
  appendixHeader: { backgroundColor: colors.summaryBg, paddingVertical: 8, paddingHorizontal: 16 },
  appendixHeaderTitle: {
    color: colors.white,
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    textAlign: 'center',
  },
  appendixBody: { padding: '8 16 10 16' },
  appendixText: { fontSize: 6.5, color: '#6b7280', lineHeight: 1.4, marginBottom: 4 },
  appendixEquationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: 6,
  },
  appendixEquationLabel: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: colors.primary },
  appendixEquationTail: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#374151' },
  appendixFraction: { alignItems: 'center' },
  appendixFractionNumerator: {
    fontSize: 7,
    color: '#374151',
    textAlign: 'center',
    paddingHorizontal: 4,
  },
  appendixFractionBar: {
    height: 0.5,
    backgroundColor: '#374151',
    width: '100%',
    minWidth: 100,
    marginVertical: 1,
  },
  appendixFractionDenominator: {
    fontSize: 7,
    color: '#374151',
    textAlign: 'center',
    paddingHorizontal: 4,
  },
});
